import { useQueries } from '@tanstack/react-query';
import { getSpecificPortfolioV3, type Portfolio } from 'venn-api';
import { useMemo } from 'react';
import { assertExhaustive, mergePortfolios } from 'venn-utils';
import { compact, groupBy } from 'lodash';
import type {
  CombinedPortfoliosState,
  CombinePortfoliosSettings,
  OrganizeOption,
  PortfolioInformation,
  StructureOption,
} from '../types';

const groupByField = (
  field: 'householdName' | 'clientName',
  portfolioInformation: PortfolioInformation[],
  portfolios: Portfolio[],
): Portfolio[] => {
  const grouped = groupBy(portfolioInformation, field);
  return Object.keys(grouped)
    .map((fieldName) => ({
      name: fieldName === 'null' ? 'Unknown' : fieldName,
      children: compact(grouped[fieldName]?.map((info) => portfolios.find((p) => p.id === info.id))),
    }))
    .filter((portfolio) => portfolio.children.length > 0) as Portfolio[];
};

const groupByFieldAtLevel = (
  field: 'householdName' | 'clientName',
  portfolioInformation: PortfolioInformation[],
  portfolios: Portfolio[],
  level: number,
): Portfolio[] => {
  if (level === 0) {
    return groupByField(field, portfolioInformation, portfolios);
  }
  return portfolios.map((portfolio) => ({
    ...portfolio,
    children: groupByFieldAtLevel(field, portfolioInformation, portfolio.children ?? [], level - 1),
  }));
};

const organizeBy = (organizeOption: OrganizeOption, portfolios: Portfolio[]) => {
  switch (organizeOption) {
    case 'PORTFOLIO':
      return portfolios;
    case 'ASSET_CLASS':
      return portfolios.map((group) => ({
        ...group,
        children: mergePortfolios(group.children),
      }));
    default:
      throw assertExhaustive(organizeOption);
  }
};

const groupPortfolios = (
  portfolios: Portfolio[],
  portfolioInformation: PortfolioInformation[],
  combinedPortfolioName: string,
  structureOption: StructureOption,
): Portfolio[] => {
  switch (structureOption) {
    case 'COMBINED':
      return [
        {
          name: combinedPortfolioName,
          children: portfolios,
        } as Portfolio,
      ];
    case 'SEPARATE':
      return portfolios;
    case 'HOUSEHOLD':
      return groupByField('householdName', portfolioInformation, portfolios);
    case 'CLIENT':
      return groupByField('clientName', portfolioInformation, portfolios);
    default:
      throw assertExhaustive(structureOption);
  }
};

export const useCombinedPortfolios = (
  portfolioInformation: PortfolioInformation[],
  combineSettings: CombinePortfoliosSettings,
): CombinedPortfoliosState => {
  const { combinedPortfolioName, organizeOption, structureOption, includeHousehold, includeClient } = combineSettings;

  const results = useQueries<Portfolio[]>({
    queries: portfolioInformation.map(({ id }) => ({
      queryKey: ['portfolio', id],
      queryFn: (): Promise<Portfolio> => getSpecificPortfolioV3(id).then((res) => res.content),
    })),
  });

  return useMemo(() => {
    if (results.some((result) => result.isLoading) || structureOption === null) {
      return {
        portfoliosLoading: true,
        portfolios: null,
      };
    }

    const portfolios = results.map((res) => res.data as Portfolio);

    let outputPortfolios = groupPortfolios(portfolios, portfolioInformation, combinedPortfolioName, structureOption);

    if (organizeOption) {
      outputPortfolios = organizeBy(organizeOption, outputPortfolios);
    }

    let depth = structureOption === 'SEPARATE' ? 0 : 1;
    if (includeHousehold) {
      outputPortfolios = groupByFieldAtLevel('householdName', portfolioInformation, outputPortfolios, depth);
      depth += 1;
    }
    if (includeClient) {
      outputPortfolios = groupByFieldAtLevel('clientName', portfolioInformation, outputPortfolios, depth);
    }

    return {
      portfolios: outputPortfolios,
      portfoliosLoading: false,
    };
  }, [
    results,
    combinedPortfolioName,
    structureOption,
    organizeOption,
    portfolioInformation,
    includeClient,
    includeHousehold,
  ]);
};
